export type AcceptedUploadTypes = Record<string, string[]>;
export type UploadFileLike = Pick<File, "type"> & Partial<Pick<File, "name">>;
export type SizedUploadFileLike = UploadFileLike & Partial<Pick<File, "size">>;
export type UploadContentTypeDefinition<T extends string> = {
  contentType: T;
  extensions: readonly string[];
};

export const AUDIO_VIDEO_MIME_TYPES = [
  "audio/mpeg",
  "audio/wav",
  "audio/mp4",
  "audio/x-m4a",
  "video/mp4",
] as const;
export const AUDIO_VIDEO_EXTENSIONS = [".mp3", ".wav", ".m4a", ".mp4"] as const;

export const KNOWLEDGE_DOCUMENT_UPLOAD_TYPES = [
  { contentType: "application/pdf", extensions: [".pdf"] },
  {
    contentType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    extensions: [".docx"],
  },
  { contentType: "text/markdown", extensions: [".md", ".markdown"] },
] as const;

export const REPORT_SAMPLE_UPLOAD_TYPES = [
  { contentType: "application/pdf", extensions: [".pdf"] },
  {
    contentType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    extensions: [".docx"],
  },
  { contentType: "image/png", extensions: [".png"] },
  { contentType: "image/jpeg", extensions: [".jpg", ".jpeg"] },
  { contentType: "text/markdown", extensions: [".md", ".markdown"] },
] as const;

export const REFERENCE_REPORT_UPLOAD_TYPES = [
  { contentType: "application/pdf", extensions: [".pdf"] },
  { contentType: "text/markdown", extensions: [".md", ".markdown"] },
] as const;

/**
 * Converts canonical upload definitions to react-dropzone's accept shape.
 */
export function toDropzoneAcceptRecord(
  acceptedTypes: readonly UploadContentTypeDefinition<string>[]
): AcceptedUploadTypes {
  return Object.fromEntries(
    acceptedTypes.map(({ contentType, extensions }) => [
      contentType,
      [...extensions],
    ])
  );
}

function isExtensionMatch(
  extension: string | undefined,
  acceptedExtensions: readonly string[]
): boolean {
  return Boolean(
    extension &&
    acceptedExtensions.some(
      (acceptedExtension) =>
        acceptedExtension.toLowerCase().replace(/^\./, "") === extension
    )
  );
}

export function getUploadFileExtension(filename?: string): string | undefined {
  if (!filename) return undefined;

  const normalizedFilename = filename.toLowerCase();
  const extension = normalizedFilename.split(".").pop();
  return extension && extension !== normalizedFilename ? extension : undefined;
}

export function isUploadExtensionAccepted(
  filename: string | undefined,
  acceptedTypes: AcceptedUploadTypes
): boolean {
  const extension = getUploadFileExtension(filename);
  if (!extension) return false;

  return Object.values(acceptedTypes).some((extensions) =>
    isExtensionMatch(extension, extensions)
  );
}

export function isFileAcceptedByDropzone(
  file: UploadFileLike,
  acceptedTypes: AcceptedUploadTypes,
  allowExtensionFallback = true
): boolean {
  return (
    Object.keys(acceptedTypes).includes(file.type) ||
    (allowExtensionFallback &&
      isUploadExtensionAccepted(file.name, acceptedTypes))
  );
}

export function isAudioUpload(file: UploadFileLike): boolean {
  return (
    AUDIO_VIDEO_MIME_TYPES.some((mimeType) => mimeType === file.type) ||
    isExtensionMatch(getUploadFileExtension(file.name), AUDIO_VIDEO_EXTENSIONS)
  );
}

export function isAudioUploadTooLarge(
  file: SizedUploadFileLike,
  maxSizeBytes: number
): boolean {
  return (
    file.size !== undefined && isAudioUpload(file) && file.size > maxSizeBytes
  );
}

/**
 * Resolves browser MIME values to the canonical content type expected by an
 * upload endpoint. The filename extension is also checked because browser MIME
 * values are not reliable for every supported upload format.
 */
export function resolveUploadContentType<T extends string>(
  contentType: string,
  filename: string | undefined,
  acceptedTypes: readonly UploadContentTypeDefinition<T>[],
  errorMessage: string
): T {
  const normalizedContentType = contentType.split(";")[0].trim().toLowerCase();
  const matchingType = acceptedTypes.find(
    (type) => type.contentType === normalizedContentType
  );
  if (matchingType) return matchingType.contentType;

  const extension = getUploadFileExtension(filename);
  const matchingContentType = acceptedTypes.find(({ extensions }) =>
    isExtensionMatch(extension, extensions)
  );

  if (matchingContentType) return matchingContentType.contentType;

  throw new Error(errorMessage);
}
